import * as Sentry from '@sentry/react';
import { createBeforeSend, type SentryUserContext } from '@lynrx/shared';
import packageJson from '../../package.json';

type Environment = 'development' | 'staging' | 'production';
type SentryInitOptions = NonNullable<Parameters<typeof Sentry.init>[0]>;
type SentryBeforeSend = NonNullable<SentryInitOptions['beforeSend']>;

function readEnv(): Environment {
  const raw = (import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE) as string;
  if (raw === 'production' || raw === 'staging') return raw;
  return 'development';
}

function readRelease(): string {
  const sha = import.meta.env.VITE_GIT_SHA;
  const version = packageJson.version;
  return sha ? `${version}+${sha}` : version;
}

/**
 * Initialise Sentry for apps/web. Safe to call exactly once at module
 * import time; subsequent calls are no-ops.
 *
 * If `VITE_SENTRY_DSN` is empty, Sentry is initialised in a no-op mode and
 * `beforeSend` returns null for every event, so dev environments without a
 * DSN never accidentally upload anything.
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;

  Sentry.init({
    ...(dsn ? { dsn } : {}),
    environment: readEnv(),
    release: readRelease(),
    sampleRate: 1.0,
    tracesSampleRate: 0.1,
    integrations: [Sentry.browserTracingIntegration()],
    sendDefaultPii: false,
    beforeSend: createBeforeSend({ dsn }) as unknown as SentryBeforeSend,
  });
}

export function setSentryUser(user: SentryUserContext): void {
  Sentry.setUser({
    id: user.id,
    tenant_id: user.tenant_id,
    user_type: user.user_type,
  });
}

export function clearSentryUser(): void {
  Sentry.setUser(null);
}

export { Sentry };
